import React from 'react';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { ProvenanceBadge } from '../trust/ProvenanceBadge';
import { UniversityMark } from './UniversityMark';
import { programmes as allProgrammes } from '../../data/admissions';
import { nextDeadline } from '../../utils/discover';
import type { UniversityProfile } from '../../types/admissions';

/**
 * The comparison itself is one component. Both compositions open the same table
 * from their own tray, so a student never sees two different answers to the same
 * question depending on which design happens to be presented.
 */
interface UniversityCompareModalProps {
  open: boolean;
  onClose: () => void;
  selected: UniversityProfile[];
}
export function UniversityCompareModal({
  open,
  onClose,
  selected
}: UniversityCompareModalProps) {
  const rows: {
    label: string;
    render: (university: UniversityProfile) => React.ReactNode;
  }[] = [{
    label: 'Location',
    render: university => `${university.city}, ${university.state}, ${university.country}`
  }, {
    label: 'Institution type',
    render: university => university.type
  }, {
    label: 'Students',
    render: university => university.students.toLocaleString()
  }, {
    label: 'Founded',
    render: university => String(university.founded)
  }, {
    label: 'International intake',
    render: university => university.internationalIntake ? 'Open to international applicants' : 'Not published'
  }, {
    label: 'On-campus housing',
    render: university => university.accommodationOnCampus ? 'Available' : 'Not available'
  }, {
    label: 'Programmes on Campuzon',
    render: university => {
      const owned = allProgrammes.filter(item => item.universityId === university.id);
      return owned.length === 0 ? 'None published yet' : `${owned.length} published`;
    }
  }, {
    label: 'Scholarships',
    render: university => `${university.scholarshipsAvailable} listed`
  }, {
    label: 'Next deadline',
    render: university => {
      const owned = allProgrammes.filter(item => item.universityId === university.id);
      const soonest = nextDeadline(owned);
      return soonest ? `${soonest.deadlineLabel} — ${soonest.title}` : 'No open deadline published';
    }
  }, {
    label: 'Published by',
    render: university => university.representative ?? 'No institution representative — entered by students'
  }, {
    label: 'Data source',
    render: university => <ProvenanceBadge provenance={university.provenance} short data-id="element-1623" data-mp="219w1qh5pknqz" />
  }];
  return <Modal open={open} onClose={onClose} title={`Comparing ${selected.length} institutions`} description="Only what each institution has actually published. An empty cell means the fact has not been published, not that the answer is no." width="lg" data-id="element-1624" data-mp="1wd5mfw1q9w6m">
      <div className="cz-tablewrap" data-id="element-1625" data-mp="3lsdgdcw240i5">
        <table className="w-full min-w-[36rem] border-collapse text-body-sm" data-id="element-1626" data-mp="3lp7mzlgeacgm">
          <thead data-id="element-1627" data-mp="3u7t56fwl591q">
            <tr data-id="element-1628" data-mp="3u68a8p6czo3c">
              <th scope="col" className="w-40 py-3 pr-4 text-left align-bottom" data-id="element-1629" data-mp="24ijidg8sgzu">
                <span className="sr-only" data-id="element-1630" data-mp="22fi6x9n31ft">Attribute</span>
              </th>
              {selected.map(university => <th key={university.id} scope="col" className="px-3 py-3 text-left align-bottom font-semibold text-ink" data-id="element-1631" data-mp="bu235lbs4evr">
                  <span className="flex items-center gap-2" data-id="element-1632" data-mp="3fb25s6mwvxc4">
                    <UniversityMark university={university} size="sm" data-id="element-1633" data-mp="3fbk6pgkiqzqd" />
                    <span className="min-w-0" data-id="element-1634" data-mp="3k93nan4ovolh">
                      <span className="block truncate" data-id="element-1635" data-mp="3kchqfxl6g1tz">{university.name}</span>
                      <span className="mt-0.5 block text-caption font-normal text-ink-muted" data-id="element-1636" data-mp="3paj8qgas3x4e">
                        {university.city}, {university.country}
                      </span>
                    </span>
                  </span>
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-line" data-id="element-1637" data-mp="2dg7hhwk6tw41">
            {rows.map(row => <tr key={row.label} data-id="element-1638" data-mp="28ifgmd1keznp">
                <th scope="row" className="py-3 pr-4 text-left align-top font-medium text-ink-muted" data-id="element-1639" data-mp="2mgm4d0csvj9n">
                  {row.label}
                </th>
                {selected.map(university => <td key={university.id} className="px-3 py-3 align-top text-ink" data-id="element-1640" data-mp="2wdidn8aiw9f9">
                    {row.render(university)}
                  </td>)}
              </tr>)}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-caption text-ink-subtle" data-id="element-1641" data-mp="315i84l7tqa4g">
        Campuzon publishes what institutions and students record here. It does not rank
        institutions, and a comparison is not advice about where to apply.
      </p>
      <Badge className="mt-3" data-id="element-1642" data-mp="3a6zps80vqjv9">Demonstration records</Badge>
    </Modal>;
}